import { Interview } from "../models/Interview";
import React, { useEffect, useState } from "react";
import "./transcriptcomponent.css";
import { fetchWithAuthentication } from "../utilities/FetchUtil";
import { InterviewResourceNotFoundError } from "../errors/InterviewResourceNotFoundError";
import { calculateDaysBetweenDates } from "../utilities/DateUtil";
import { Loader, Stack, Text } from "@mantine/core";

interface TranscriptComponentProps {
  interview: Interview;
  height: string;
}

const TranscriptComponent = ({
  interview,
  height,
}: TranscriptComponentProps) => {
  const [transcription, setTranscription] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const today = new Date();
  const dateOfCreation = new Date(interview.createdAt);
  const daysPassed = calculateDaysBetweenDates(today, dateOfCreation);

  const getSpeakerLabel = (label: string) => {
    try {
      const parts = label.split("_");
      if (parts.length !== 2 || isNaN(Number(parts[1]))) return label;

      return `Speaker ${Number(parts[1]) + 1}`;
    } catch {
      return label;
    }
  };

  useEffect(() => {
    const fetchTranscription = async () => {
      setLoading(true);
      if (daysPassed >= 180) {
        setLoading(false);
        setTranscription(
          "Transcription has been deleted due to the interview being over 180 days ago.",
        );
        return;
      }
      try {
        fetchWithAuthentication(
          `/interviews/transcription?userId=${interview.userId}&interviewId=${interview.interviewId}`,
          "GET",
          null,
        )
          .then((response) => {
            if (!response.ok) {
              if (response.status === 404) {
                setTranscription(
                  "Oops! No transcript exists for this interview.",
                );
              }
              throw new InterviewResourceNotFoundError(
                `HTTP error! Status: ${response.status}`,
              );
            }
            return response.text(); // Get the presigned URL
          })
          .then((presignedUrl) => fetch(presignedUrl))
          .then((response) => {
            console.log(response.status);
            if (!response.ok) {
              throw new Error(
                `Response from S3 was non-200: ${response.status}`,
              );
            }
            return response.text();
          })
          .then((text) => {
            const parsedText = JSON.parse(text);
            let transcript: string = "";

            if (parsedText.results?.audio_segments?.length) {
              transcript = parsedText.results.audio_segments
                .map(
                  (segment: { speaker_label: string; transcript: string }) =>
                    `${getSpeakerLabel(segment.speaker_label)}: ${segment.transcript}`,
                )
                .join("\n");
              setTranscription(transcript);
            } else {
              setTranscription("Transcript has no content!");
            }

            return transcript;
          })
          .catch((error) => {
            if (!(error instanceof InterviewResourceNotFoundError)) {
              setTranscription(
                "Unable to load transcription. Please refresh the page and try again.",
              );
            }
            console.error("Transcript Fetch error:", error.message);
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (error) {
        console.error("Error fetching transcription:", error);
      }
    };

    fetchTranscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log(transcription);
  }, [transcription]);

  return (
    <Stack
      h={height}
      maw={500}
      p="xs"
      style={{
        overflowY: "auto",
        overflowX: "hidden",
      }}
      className="transcript-container"
    >
      {transcription ? (
        transcription.split("\n").map((line, i) => (
          <React.Fragment key={i}>
            <Text
              span
              dangerouslySetInnerHTML={{
                __html: line.replace(/(Speaker \d+)/g, "<strong>$1</strong>"),
              }}
            />
            <br />
          </React.Fragment>
        ))
      ) : transcription === "" && !loading ? (
        <Text>No transcript</Text>
      ) : (
        <Loader size="md" />
      )}
    </Stack>
  );
};

export default TranscriptComponent;
